import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { DataGridPro, GridColDef, GridPaginationModel } from '@mui/x-data-grid-pro';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Fade from '@mui/material/Fade';
import Popper from '@mui/material/Popper';
import { useAuth } from 'oidc-react';
import { ReportRow, ReportType } from '../../../models/reportOverviewRow';
import { ReportOverviewItem, updateFavoriteReportOverview } from '../../../api/reportOverview';
import ToolbarReportOverview from './ToolbarReportOverview';
import { MILLIS_PER_DAY } from '../../../helpers/dateUtils';
import GenerateReportButton from '../components/GenerateReportButton';
import GenerateReportOverviewPanel from '../components/GenerateReportOverviewPanel';
import EnhancedSnackbar, { SnackbarConfig } from '../../../components/EnhancedSnackbar';
import EmptyResult from '../../../components/EmptyResult';


function isRecent(dateString: string): boolean {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMillis = now.getTime() - date.getTime();
    return diffInMillis <= MILLIS_PER_DAY * 30;
}


export const getReportColumns = (
    onToggleFavourite: (id: number, isFavourite: boolean) => void,
    onGenerate: (row: ReportRow) => void,
    setOpenRowId: (id: number | null) => void,
    setOpenType: (type: ReportType) => void,
    t: (key: string) => string,
    enableScheduledFeature = true
): GridColDef<ReportRow>[] => [
        {
            field: 'isFavourite',
            headerName: '',
            width: 80,
            sortable: false,
            renderCell: ({ row }) => {
                const Icon = row.isFavourite ? StarIcon : StarBorderIcon;

                return (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%'
                        }}
                    >
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                onToggleFavourite(row.id, row.isFavourite);
                            }}
                        >
                            <Icon color={row.isFavourite ? 'primary' : 'inherit'} />
                        </IconButton>
                    </Box>
                );
            },
        },
        {
            field: 'name',
            headerName: '',
            flex: 1,
            renderCell: ({ row }) => (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {t(`screens.dataDownloader.reportType.${row.displayKey}`)}
                            {isRecent(row.addedOn) ? (
                                <Chip
                                    color='primary'
                                    size='small'
                                    label='New'
                                    sx={{
                                        padding: '2px',
                                        maxHeight: '18px',
                                        '& .MuiChip-label': {
                                            fontWeight: 600,
                                            fontSize: '10px',
                                            letterSpacing: '0px',
                                            padding: '0 4px',
                                            lineHeight: '14px',
                                        },
                                    }}
                                />
                            ) : null}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t(`screens.dataDownloader.reportType.${row.descDisplayKey}`)}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', marginLeft: 'auto' }}>
                        {row.isManual && <GenerateReportButton row={row} type="Manual" setOpenRowId={setOpenRowId} setOpenType={setOpenType} onClick={() => onGenerate(row)} />}
                        {row.isAutomated && enableScheduledFeature && <GenerateReportButton row={row} type="Scheduled" setOpenRowId={setOpenRowId} setOpenType={setOpenType} onClick={() => onGenerate(row)} />}
                    </Box>
                </Box>
            ),
        },
    ];

interface Props {
    reports: ReportRow[];
    reportTypes?: ReportOverviewItem[];
    showNoFavourites?: boolean;
    onNullFavorites?: () => void;
    refreshData: () => void;
}

const ReportOverviewTable = ({ reports, reportTypes, onNullFavorites, showNoFavourites, refreshData }: Props) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const enableScheduledFeature = process.env.REACT_APP_ENABLE_DOWNLOADER_SCHEDULED_FEATURE === 'true';
    const [openRowId, setOpenRowId] = useState<number | null>(null);
    const [openType, setOpenType] = useState<ReportType>(null);
    const anchorRef = useRef<HTMLDivElement>(null);
    const [rows, setRows] = useState<ReportRow[]>(reports);
    const [page, setPage] = useState(0);
    const [searchValue, setSearchValue] = useState('');
    const [openRow, setOpenRow] = useState<ReportRow | null>(null);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const auth = useAuth();
    const maybeAccessToken = auth?.userData?.access_token;

    const [snackbar, setSnackbarConfig] = useState<SnackbarConfig>({
        open: false,
        severity: 'info',
        message: '',
    });

    useEffect(() => {
        setRows(reports);
    }, [reports]);

    useEffect(() => {
        setPage(0);
    }, [searchValue]);

    const handleToggleFavourite = useCallback(
        async (id: number, isFavourite: boolean) => {
            setRows((prev) =>
                prev.map((r) =>
                    r.id === id ? { ...r, isFavourite: !isFavourite } : r
                )
            );
            const res = updateFavoriteReportOverview(id, !isFavourite, maybeAccessToken)
                .then(() => {
                    refreshData();
                    const row = rows.find((r) => r.id === id);
                    setSnackbarConfig({
                        open: true,
                        severity: 'success',
                        message: t('screens.dataDownloader.favouriteUpdateSuccess', { reportName: t(`screens.dataDownloader.reportType.${row?.displayKey}`) }),
                    });
                })
                .catch((error: any) => {
                    setRows((prev) =>
                        prev.map((r) =>
                            r.id === id ? { ...r, isFavourite } : r
                        )
                    );
                    setSnackbarConfig({
                        open: true,
                        severity: 'error',
                        message: t('screens.dataDownloader.favouriteUpdateError'),
                    });
                })
        },
        [queryClient, updateFavoriteReportOverview, rows, t]
    );

    const filteredReports = useMemo(
        () =>
            rows.filter((report) => {
                const matchesSearch = report.displayKey.toLowerCase().includes(searchValue.toLowerCase());

                const isAutomaticOnly = report.isAutomated && !report.isManual;
                const shouldHide = !enableScheduledFeature && isAutomaticOnly;

                return matchesSearch && !shouldHide;
            }),
        [rows, searchValue, enableScheduledFeature]
    );


    const columns = useMemo(() => getReportColumns(handleToggleFavourite, setOpenRow, setOpenRowId, setOpenType, t, enableScheduledFeature), [
        handleToggleFavourite,
        t,
        enableScheduledFeature,
    ]);

    const handlePaginationModelChange = useCallback(
        (model: GridPaginationModel) => {
            setPage(model.page);
            setRowsPerPage(model.pageSize);
        },
        []
    );
const shouldShowGrid = !showNoFavourites;

    return (
        <Box sx={{ flex: 1, width: '100%', height: '100%' }}>
            <Paper
                sx={{
                    height: '70vh',
                    width: '100%',
                    display: 'flex',
                    flexFlow: 'column',
                    borderRadius: 3,
                    overflow: 'hidden',
                    marginBottom: '60px'
                }}
                variant="outlined"
            >
                {showNoFavourites && (
                    <EmptyResult
                        title={t('components.iconMessage.noReports.noFavourites')}
                        subtitle={t('components.iconMessage.noReports.subtitleNoFavourite')}
                        sx={{ borderTop: `1px solid ${theme.palette.divider}` }}
                        button={{
                            text: t('components.iconMessage.noReports.buttonFavourite'),
                            onClick: () => {
                                if (onNullFavorites) {
                                    onNullFavorites();
                                }
                                setSearchValue('');
                            }
                        }}
                    />
                )}
                {shouldShowGrid && (
                    <DataGridPro
                        ref={anchorRef}
                        rows={filteredReports}
                        columns={columns}
                        getRowId={(row) => row.id}
                        pagination
                        getRowHeight={() => 'auto'}
                        getEstimatedRowHeight={() => 100}
                        disableColumnSorting
                        pageSizeOptions={[10, 25, 50, 100]}
                        onPaginationModelChange={handlePaginationModelChange}
                        columnHeaderHeight={0}
                        initialState={{
                            pagination: {
                                paginationModel: {
                                    page,
                                    pageSize: rowsPerPage,
                                },
                            },
                        }}
                        paginationModel={{ page, pageSize: rowsPerPage }}
                        localeText={{
                            MuiTablePagination: {
                                labelRowsPerPage: t('components.filters.rowsPerPage'),
                            },
                            footerRowSelected: () => '',
                        }}
                        disableColumnMenu
                        disableMultipleRowSelection
                        slots={{
                            toolbar: ToolbarReportOverview,
                        }}
                        slotProps={{
                            toolbar: {
                                searchValue,
                                onSearchChange: setSearchValue,
                            },
                        }}

                        sx={{
                            flex: 1,
                            width: '100%',
                            borderTopLeftRadius: 0,
                            borderTopRightRadius: 0,
                            '& .MuiDataGrid-row:hover': {
                                backgroundColor: theme.palette.secondary.main,
                                transition: 'background-color 0.2s ease',
                            },
                            '& .MuiDataGrid-columnHeaders': {
                                display: 'none',
                            },
                            '& .MuiDataGrid-toolbarContainer': {
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                            },
                            '& .MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within, .MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within': {
                                outline: 'none',
                                backgroundColor: theme?.palette?.grey?.['50p'],
                            },
                            '& .MuiDataGrid-columnHeaderTitle': {
                                fontWeight: 600,
                            },
                            '& .MuiDataGrid-columnHeader, .MuiDataGrid-scrollbarFiller, .MuiDataGrid-filler': {
                                backgroundColor: 'background.paper',
                            },
                            '& ::-webkit-scrollbar': {
                                width: '6px',
                                height: '6px',
                            },
                            '& ::-webkit-scrollbar-thumb': {
                                borderRadius: '10px',
                                backgroundColor: theme?.palette?.grey?.['50p'],
                            },
                            '& .MuiDataGrid-cell': {
                                minHeight: '52px',
                                paddingTop: '16px',
                                paddingBottom: '16px',
                            },
                            '& .MuiDataGrid-cell:focus': {
                                minHeight: '52px',
                                paddingTop: '16px',
                                paddingBottom: '16px',
                                backgroundColor: theme?.palette?.grey?.['50p'],
                            },
                        }}
                    />)}
                <Popper
                    open={openRowId !== null}
                    placement="bottom-end"
                    transition
                >
                    <Fade mountOnEnter unmountOnExit in={openRowId !== null}>
                        <Paper
                            sx={{
                                width: '400px',
                                height: 'calc(100vh - 64px)',
                                position: 'fixed',
                                right: 0,
                                bottom: 0,
                                borderRadius: '0px',
                                boxShadow:
                                    '0px 8px 10px -5px rgba(0, 0, 0, 0.2), 0px 16px 24px 2px rgba(0, 0, 0, 0.14), 0px 6px 30px 5px rgba(0, 0, 0, 0.12)',
                            }}
                        >
                            {openRow && (
                                <GenerateReportOverviewPanel
                                    reportSource={{
                                        ...openRow,
                                        type: openType || 'Manual'
                                    }}
                                    close={(snackbarMessage?: { message: string, severity: 'success' | 'error' }) => {
                                        setOpenRowId(null);
                                        if (snackbarMessage?.message) {
                                            setSnackbarConfig({ open: true, severity: snackbarMessage.severity, message: snackbarMessage.message });
                                        }
                                    }}
                                    reportTypes={reportTypes}
                                />
                            )}
                        </Paper>
                    </Fade>
                </Popper>
            </Paper>

            <EnhancedSnackbar
                open={snackbar.open}
                severity={snackbar.severity}
                message={snackbar.message}
                onClose={() => setSnackbarConfig({ ...snackbar, open: false })}
                forceColor={
                    snackbar.severity === 'success'
                        ? theme.palette.success['160p']
                        : theme.palette.error['160p']
                }
            />
        </Box>
    );
};

export default ReportOverviewTable;
